import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { DateTools, TTableRow, StorageKey, StorageService, Button } from '@treezor/design-system';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { cardSubTypes, ICardTransaction } from '@/features/cards/store/models/card.model';
import { PER_PAGE_TRANSACTIONS } from '@/features/cards/constants';

import { CellLink } from '@/ui-kit/table-filters/CellLink';
import { RowSkeleton } from '@/ui-kit/loaders/RowSkeleton';
import {
  TCardTransactionColumnSetting,
  TCardTransactionColumnConfig,
  TCardTransactionRowType,
  TCardTransactionColumnKey,
} from '@/features/cards/store/models/card-transactions.model';
import { PaymentModeCell } from '@/features/cards/components/CardTransactionsTable/PaymentModeCell';
import { PaymentAmountCell } from '@/features/cards/components/CardTransactionsTable/PaymentAmountCell';
import { PaymentStatusCell } from '@/features/cards/components/CardTransactionsTable/PaymentStatusCell';
import { useMccRef } from '@/hooks/useMccRef';
import { PaymentMccCell } from '@/features/cards/components/CardTransactionsTable/PaymentMccCell';
import { PaymentMerchantCell } from '@/features/cards/components/CardTransactionsTable/PaymentMerchantCell';
import { mappingIso3Countries } from '@/utils/constants';
import { PaymentMerchantCategoryCell } from '@/features/cards/components/CardTransactionsTable/PaymentMerchantCategoryCell';
import { PaymentAuthorizationCell } from '@/features/cards/components/CardTransactionsTable/PaymentAuthorizationCell';
import { PaymentTruncateCell } from '@/features/cards/components/CardTransactionsTable/PaymentTruncateCell';
import { PaymentCardholderPresenceCell } from '@/features/cards/components/CardTransactionsTable/PaymentCardholderPresenceCell';
import { PaymentCardPresenceCell } from '@/features/cards/components/CardTransactionsTable/PaymentCardPresenceCell';
import { PaymentPANEntryMethodCell } from '@/features/cards/components/CardTransactionsTable/PaymentPANEntryMethodCell';
import { Payment3DSCell } from '@/features/cards/components/CardTransactionsTable/Payment3DSCell';

interface ICardTransactionRowsProps {
  transactions?: ICardTransaction[];
  headersList?: TCardTransactionColumnKey[];
  columnsConfig?: TCardTransactionColumnConfig;
  isLoading?: boolean;
  onRowClick?: (transaction: ICardTransaction) => void;
}

export function CardTransactionRows({
  transactions,
  headersList,
  columnsConfig,
  isLoading = false,
  onRowClick,
}: ICardTransactionRowsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentLang = StorageService.get(StorageKey.LANGUAGE);

  const mccRefs = useMccRef();

  const getIssuerDateTime = useCallback((dateString: string) => {
    const date = new Date(dateString);
    date.setHours(date.getHours() + 1);

    return DateTools.format(date, 'date-time');
  }, []);

  const handleNavigateToWallet = useCallback(
    (userId: number, walletId: number) => {
      navigate(DashboardHelpers.generateProfilePath(userId!, { walletId }));
    },
    [navigate],
  );

  const handleNavigateToCard = useCallback(
    (userId: number, cardId: number) => {
      navigate(DashboardHelpers.generateProfilePath(userId!, { cardId }));
    },
    [navigate],
  );

  const getMccData = useCallback(
    (mccCode: string) => {
      const mccData = (mccRefs || []).find((mcc) => mcc.MCC.toString() === mccCode.toString());
      if (mccData)
        return {
          mccCode,
          mccDescription: mccData[currentLang === 'fr' ? 'DescriptionFR' : 'DescriptionEN'],
        };
      return { mccCode };
    },
    [mccRefs, currentLang],
  );

  const getCellRenderer = useCallback(
    (key: TCardTransactionColumnKey, config: TCardTransactionColumnSetting, transaction: ICardTransaction) => {
      switch (key) {
        case 'publicToken':
          return (
            <Button
              size="s"
              variant="ghost"
              onClick={() => handleNavigateToCard(transaction.userId, transaction.cardId)}
            >
              {transaction.publicToken}
            </Button>
          );
        case 'cardtransactionId':
          return <PaymentModeCell transaction={transaction} config={config} />;
        case 'authorizationIssuerTime':
          return (
            <span className="text-secondary typo-body-regular-small">
              {getIssuerDateTime(transaction.authorizationIssuerTime)}
            </span>
          );
        case 'paymentAmount':
          return <PaymentAmountCell transaction={transaction} config={config} />;
        case 'paymentCountry':
          return transaction?.paymentCountry ? (
            <span>{t(`shared.countries.${mappingIso3Countries[transaction.paymentCountry]}`)}</span>
          ) : (
            <>/</>
          );
        case 'paymentStatus':
          return <PaymentStatusCell status={transaction.paymentStatus} />;
        case 'transactionSubType':
          return (
            <span className="typo-body-regular-paragraph text-secondary">
              {cardSubTypes.includes(transaction?.transactionSubType)
                ? [
                    transaction.transactionSubType,
                    t(
                      `pages.currentProfiles.cards.transactions.table.headers.subType.${transaction.transactionSubType}`,
                    ),
                  ].join(': ')
                : '/'}
            </span>
          );
        case 'walletId':
          return (
            <CellLink
              label={`${transaction.walletId}`}
              handleNavigate={() => handleNavigateToWallet(transaction.userId, transaction.walletId)}
              testId="redirect-walletId"
            />
          );

        case 'mccCode':
          return <PaymentMccCell {...getMccData(transaction.mccCode)} config={config} />;
        case 'merchant':
          return <PaymentMerchantCell transaction={transaction} config={config} />;
        case 'merchantCategory':
          return <PaymentMerchantCategoryCell transaction={transaction} config={config} />;
        case 'authorizationResponseCode':
          return <PaymentAuthorizationCell transaction={transaction} config={config} />;
        case 'authorizationNote': {
          return <PaymentTruncateCell textString={transaction?.authorizationNote} />;
        }
        case 'posCardholderPresence': {
          return <PaymentCardholderPresenceCell posCardholderPresence={transaction?.posCardholderPresence} />;
        }
        case 'posCardPresence': {
          return <PaymentCardPresenceCell posCardPresence={transaction?.posCardPresence} />;
        }
        case 'panEntryMethod': {
          return <PaymentPANEntryMethodCell entryMethod={transaction?.panEntryMethod} />;
        }
        case 'is3DS': {
          return <Payment3DSCell transaction={transaction} config={config} />;
        }

        default:
          return (
            <PaymentTruncateCell
              className="text-tertiary typo-body-regular-small"
              textString={transaction[key]?.toString()}
            />
          );
      }
    },
    [getIssuerDateTime, getMccData, handleNavigateToCard, handleNavigateToWallet, t],
  );

  if (isLoading) {
    return RowSkeleton<TCardTransactionRowType>({ headers: headersList, length: PER_PAGE_TRANSACTIONS });
  }

  return transactions?.map((transaction: ICardTransaction): TTableRow<TCardTransactionRowType> => {
    const allRows = Object.entries(columnsConfig).map(([key, config]) => [
      key,
      getCellRenderer(key as TCardTransactionColumnKey, config, transaction),
    ]);

    return { ...Object.fromEntries(allRows), onClick: onRowClick ? () => onRowClick(transaction) : undefined };
  });
}
